import React from 'react';
import { COMPANY } from '../data/ItineraryData';

/**
 * This component renders a beautiful A4 travel poster.
 * It is hidden off-screen and captured by html2canvas for PDF export.
 */
const ItineraryPoster = React.forwardRef(({ data, logoSrc }, ref) => {
    const colors = {
        dark: '#1f3a2d',
        green: '#3e866b',
        gold: '#d4a437',
        light: '#f0faf5',
        text: '#1a1a2e',
        muted: '#4b5563',
    };

    const styles = {
        /* ─ root poster ─────────────────────────── */
        poster: {
            width: '794px',          /* A4 @ 96dpi */
            minHeight: '1123px',
            background: '#ffffff',
            fontFamily: "'Segoe UI', Arial, sans-serif",
            color: colors.text,
            position: 'relative',
            overflow: 'hidden',
        },

        /* ─ hero ──────────────────────────────── */
        hero: {
            width: '100%',
            height: '320px',
            position: 'relative',
            overflow: 'hidden',
        },
        heroImg: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
        heroOverlay: {
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to right, rgba(10,25,18,0.92) 40%, rgba(0,0,0,0.35) 100%)',
        },
        heroContent: {
            position: 'absolute',
            top: 0, left: 0, bottom: 0,
            padding: '28px 36px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
        },
        heroTop: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
        logoBox: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
        },
        logoImg: {
            width: '52px',
            height: '52px',
            objectFit: 'contain',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '4px',
        },
        logoText: {
            display: 'flex',
            flexDirection: 'column',
        },
        logoName: {
            fontSize: '15px',
            fontWeight: '800',
            color: '#fff',
            lineHeight: 1.1,
        },
        logoTag: {
            fontSize: '8px',
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
        },
        regionBadge: {
            background: colors.gold,
            color: colors.dark,
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            padding: '4px 14px',
            borderRadius: '20px',
        },
        heroBottom: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
        },
        destName: {
            fontSize: '52px',
            fontWeight: '900',
            color: '#ffffff',
            lineHeight: 1.0,
            letterSpacing: '-1px',
            textShadow: '0 3px 12px rgba(0,0,0,0.5)',
        },
        destTagline: {
            fontSize: '13px',
            color: 'rgba(255,255,255,0.8)',
            marginTop: '4px',
            fontStyle: 'italic',
        },
        priceBox: {
            background: colors.gold,
            color: colors.dark,
            borderRadius: '14px',
            padding: '10px 18px',
            textAlign: 'right',
        },
        priceFrom: { fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.7 },
        priceVal: { fontSize: '22px', fontWeight: '900', lineHeight: 1 },
        priceUnit: { fontSize: '9px', opacity: 0.7 },

        /* ─ meta strip ────────────────────────── */
        metaStrip: {
            background: colors.dark,
            display: 'flex',
            justifyContent: 'space-around',
            padding: '12px 24px',
        },
        metaItem: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#fff',
        },
        metaLabel: { fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px' },
        metaVal: { fontSize: '11px', fontWeight: '700', marginTop: '2px' },

        /* ─ body ──────────────────────────────── */
        body: { padding: '22px 32px 0' },

        /* ─ two-col ───────────────────────────── */
        twoCol: { display: 'flex', gap: '20px', marginBottom: '18px' },
        colLeft: { flex: '1 1 0' },
        colRight: { width: '235px', flexShrink: 0 },

        /* ─ section title ────────────────────── */
        secTitle: {
            fontSize: '11px',
            fontWeight: '800',
            color: colors.green,
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            borderLeft: `3px solid ${colors.gold}`,
            paddingLeft: '8px',
            marginBottom: '8px',
        },

        /* ─ overview ─────────────────────────── */
        overview: {
            fontSize: '10.5px',
            color: colors.muted,
            lineHeight: 1.65,
            marginBottom: '16px',
        },

        /* ─ highlights ───────────────────────── */
        hlGrid: { display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '16px' },
        hlChip: {
            fontSize: '9.5px',
            fontWeight: '600',
            color: colors.dark,
            background: colors.light,
            border: `1px solid #b8dfc9`,
            borderRadius: '20px',
            padding: '4px 10px',
        },

        /* ─ day plans ────────────────────────── */
        dayRow: {
            display: 'flex',
            gap: '6px',
            alignItems: 'flex-start',
            marginBottom: '7px',
            background: '#f8fbf9',
            borderRadius: '8px',
            padding: '7px 10px',
            borderLeft: `3px solid ${colors.green}`,
        },
        dayBadge: {
            background: colors.dark,
            color: colors.gold,
            fontSize: '8px',
            fontWeight: '800',
            borderRadius: '10px',
            padding: '2px 7px',
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        dayTitle: { fontSize: '10px', fontWeight: '700', color: colors.dark },
        dayActivities: { fontSize: '9px', color: colors.muted, marginTop: '2px', lineHeight: 1.5 },

        /* ─ inc/exc ──────────────────────────── */
        incBox: {
            background: '#f0faf5',
            border: '1.5px solid #c6e8d4',
            borderRadius: '12px',
            padding: '12px 14px',
            marginBottom: '12px',
        },
        excBox: {
            background: '#fff5f5',
            border: '1.5px solid #fcc',
            borderRadius: '12px',
            padding: '12px 14px',
        },
        incTitle: { fontSize: '10px', fontWeight: '800', color: '#1f6e44', marginBottom: '6px' },
        excTitle: { fontSize: '10px', fontWeight: '800', color: '#c0392b', marginBottom: '6px' },
        incItem: { fontSize: '9px', color: '#2d6a50', padding: '2px 0', borderBottom: '1px dashed #c6e8d4', display: 'flex', gap: '5px' },
        excItem: { fontSize: '9px', color: '#8b2222', padding: '2px 0', borderBottom: '1px dashed #fcc', display: 'flex', gap: '5px' },

        /* ─ gallery row ──────────────────────── */
        galleryRow: {
            display: 'flex',
            gap: '8px',
            marginBottom: '18px',
        },
        galleryImg: {
            flex: 1,
            height: '90px',
            objectFit: 'cover',
            borderRadius: '8px',
        },

        /* ─ footer ───────────────────────────── */
        footer: {
            background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.green} 100%)`,
            padding: '16px 32px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '18px',
        },
        footerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
        footerLogo: { width: '40px', height: '40px', objectFit: 'contain', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', padding: '3px' },
        footerName: { fontSize: '12px', fontWeight: '800', color: '#fff' },
        footerCert: { fontSize: '7.5px', color: 'rgba(255,255,255,0.55)', marginTop: '1px' },
        footerContacts: { display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end' },
        footerContact: { fontSize: '9px', color: 'rgba(255,255,255,0.8)' },

        /* ─ decorative stripe ────────────────── */
        stripe: {
            height: '5px',
            background: `linear-gradient(to right, ${colors.dark} 0%, ${colors.dark} 33%, ${colors.gold} 33%, ${colors.gold} 66%, #7a4b2b 66%, #7a4b2b 100%)`,
        },
    };

    return (
        <div ref={ref} style={styles.poster}>

            {/* ── HERO ── */}
            <div style={styles.hero}>
                <img
                    src={data.images[0]}
                    alt={data.destination}
                    style={styles.heroImg}
                    crossOrigin="anonymous"
                />
                <div style={styles.heroOverlay} />
                <div style={styles.heroContent}>
                    <div style={styles.heroTop}>
                        <div style={styles.logoBox}>
                            <img src={logoSrc} alt="logo" style={styles.logoImg} crossOrigin="anonymous" />
                            <div style={styles.logoText}>
                                <span style={styles.logoName}>{COMPANY.name}</span>
                                <span style={styles.logoTag}>{COMPANY.tagline}</span>
                            </div>
                        </div>
                        <span style={styles.regionBadge}>{data.region}</span>
                    </div>
                    <div style={styles.heroBottom}>
                        <div>
                            <div style={styles.destName}>{data.destination}</div>
                            <div style={styles.destTagline}>✈ {data.tagline}</div>
                        </div>
                        <div style={styles.priceBox}>
                            <div style={styles.priceFrom}>Starting From</div>
                            <div style={styles.priceVal}>{data.priceFrom}</div>
                            <div style={styles.priceUnit}>per person</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── META STRIP ── */}
            <div style={styles.metaStrip}>
                {[
                    { label: 'Duration', val: data.duration },
                    { label: 'Hotel', val: data.hotel },
                    { label: 'Meals', val: data.meals },
                    { label: 'Best Time', val: data.bestTime },
                ].map((m, i) => (
                    <div key={i} style={styles.metaItem}>
                        <span style={styles.metaLabel}>{m.label}</span>
                        <span style={styles.metaVal}>{m.val}</span>
                    </div>
                ))}
            </div>

            {/* ── BODY ── */}
            <div style={styles.body}>

                {/* Overview */}
                <p style={styles.overview}>{data.overview}</p>

                {/* Highlights */}
                <div style={styles.secTitle}>🌟 Trip Highlights</div>
                <div style={styles.hlGrid}>
                    {data.highlights.map((h, i) => (
                        <span key={i} style={styles.hlChip}>✓ {h}</span>
                    ))}
                </div>

                {/* Gallery row */}
                <div style={styles.galleryRow}>
                    {data.images.map((img, i) => (
                        <img key={i} src={img} alt={`view ${i + 1}`} style={styles.galleryImg} crossOrigin="anonymous" />
                    ))}
                </div>

                {/* Two column: Days + Inc/Exc */}
                <div style={styles.twoCol}>

                    {/* ─ Day Plans ─ */}
                    <div style={styles.colLeft}>
                        <div style={styles.secTitle}>📅 Day-Wise Plan</div>
                        {data.days.map((d, i) => (
                            <div key={i} style={styles.dayRow}>
                                <div>
                                    <span style={styles.dayBadge}>Day {d.day}</span>
                                </div>
                                <div>
                                    <div style={styles.dayTitle}>{d.icon} {d.title}</div>
                                    <div style={styles.dayActivities}>
                                        {d.activities.slice(0, 3).join(' · ')}
                                        {d.activities.length > 3 ? ` ... +${d.activities.length - 3} more` : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ─ Inc / Exc ─ */}
                    <div style={styles.colRight}>
                        <div style={styles.incBox}>
                            <div style={styles.incTitle}>✅ What's Included</div>
                            {data.inclusions.map((item, i) => (
                                <div key={i} style={styles.incItem}>
                                    <span>✔</span><span>{item}</span>
                                </div>
                            ))}
                        </div>
                        <div style={styles.excBox}>
                            <div style={styles.excTitle}>❌ Not Included</div>
                            {data.exclusions.map((item, i) => (
                                <div key={i} style={styles.excItem}>
                                    <span>✘</span><span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>

            {/* ── TRICOLOR STRIPE ── */}
            <div style={styles.stripe} />

            {/* ── FOOTER ── */}
            <div style={styles.footer}>
                <div style={styles.footerLeft}>
                    <img src={logoSrc} alt="logo" style={styles.footerLogo} crossOrigin="anonymous" />
                    <div>
                        <div style={styles.footerName}>{COMPANY.name}</div>
                        <div style={styles.footerCert}>{COMPANY.cert}</div>
                    </div>
                </div>
                <div style={styles.footerContacts}>
                    <span style={styles.footerContact}>📞 {COMPANY.phone}  |  {COMPANY.altPhone}</span>
                    <span style={styles.footerContact}>✉️ {COMPANY.email}  |  🌐 {COMPANY.website}</span>
                    <span style={styles.footerContact}>📍 {COMPANY.address}</span>
                </div>
            </div>

        </div>
    );
});

export default ItineraryPoster;
